// Plain-text descriptions of a workflow graph and of a solution — what NoVA
// reads when it has to explain, guide or edit one. Browser-safe (no Node
// imports); the generator copies this file to frontend/src/data/ so the
// editor can stamp the same text on the screen (data-nova-describe) that
// the describe_pipeline / solution_status composers return. One source,
// one vocabulary on both sides. [[nqube-logic-and-requirements]]
import { agentOsProblems, isModelBlock, sideEffectKind, portsOf } from './agentosGraph.js';
import { requirementProblems, requirementSummary, estimateGraph } from './requirements.js';

const clip = (s, n) => { const t = String(s ?? ''); return t.length > n ? `${t.slice(0, n - 1)}…` : t; };
const label = (b) => b?.label || b?.type || b?.id || 'block';

// One line per block: `id · type "label" [ports] (flags)`; config summary on
// request. Flags: model · side-effect · gate · placeholder · sensitive.
function blockLine(b, { blockTypes = null, selected = null, verbose = false, run = null } = {}) {
  const def = blockTypes?.[b.type] || null;
  const ports = portsOf(b, def);
  const flags = [];
  if (isModelBlock(b)) flags.push('model');
  if (sideEffectKind(b.type)) flags.push(`side-effect:${sideEffectKind(b.type)}`);
  if (b.type === 'design.placeholder') flags.push('PLACEHOLDER');
  if (b.classification === 'sensitive') flags.push('sensitive');
  if (b.id === selected) flags.push('SELECTED');
  if (run?.status) flags.push(`run:${run.status}`);
  const cfg = verbose && b.config
    ? Object.entries(b.config).filter(([, v]) => v !== '' && v != null).map(([k, v]) => `${k}=${clip(String(v).replace(/\s+/g, ' '), 80)}`).join(', ')
    : '';
  return `- ${b.id} · ${b.type} "${label(b)}"${ports?.length ? ` ports[${ports.join('|')}]` : ''}${flags.length ? ` (${flags.join(', ')})` : ''}${cfg ? ` {${cfg}}` : ''}`;
}

// describeGraph(graph, opts) → text. opts: { requirements, policy, capabilities,
// selected, lastRun, blockTypes, maxChars (default unbounded), verbose }.
// Short form (maxChars ~900) is for the screen outline; the full form is the
// composer's return.
export function describeGraph(graph, opts = {}) {
  const { requirements = [], policy = null, capabilities = [], selected = null, lastRun = null, blockTypes = null, maxChars = 0, verbose = false, runtime = null, name = '' } = opts;
  const blocks = graph?.blocks || [];
  const conns = graph?.connections || [];
  const byId = Object.fromEntries(blocks.map((b) => [b.id, b]));
  const env = policy?.env || 'sandbox';
  const lines = [];
  lines.push(`Workflow${name ? ` "${name}"` : ''}: ${blocks.length} block${blocks.length === 1 ? '' : 's'}, ${conns.length} connection${conns.length === 1 ? '' : 's'}, env ${env}.`);
  if (!blocks.length) {
    lines.push('The canvas is empty — add blocks from the palette or ask NoVA to build / edit the workflow.');
    return lines.join('\n');
  }
  const placeholders = blocks.filter((b) => b.type === 'design.placeholder');
  const sensitive = blocks.filter((b) => b.classification === 'sensitive');
  const models = blocks.filter(isModelBlock);
  const effects = blocks.filter((b) => sideEffectKind(b.type) && b.type !== 'control.preflight');
  lines.push(`Shape: ${models.length} model block${models.length === 1 ? '' : 's'}, ${effects.length} side effect${effects.length === 1 ? '' : 's'}${placeholders.length ? `, ${placeholders.length} placeholder${placeholders.length === 1 ? '' : 's'} (sandbox only)` : ''}${sensitive.length ? `, ${sensitive.length} sensitive input${sensitive.length === 1 ? '' : 's'}` : ''}.`);
  lines.push('Blocks:');
  for (const b of blocks) lines.push(blockLine(b, { blockTypes, selected, verbose, run: runtime?.[b.id] || null }));
  lines.push('Connections:');
  for (const c of conns) lines.push(`- ${c.from}${c.fromPort ? `[${c.fromPort}]` : ''} → ${c.to}${!byId[c.from] || !byId[c.to] ? ' (dangling)' : ''}`);
  if (capabilities?.length) lines.push(`Capabilities: ${capabilities.map((c) => `${c.id} (${c.kind}${c.target ? ` → ${clip(c.target, 60)}` : ''})`).join(' · ')}`);
  const problems = agentOsProblems(graph, capabilities || [], policy || { env });
  if (problems.length) lines.push(`agentOS problems (block a save/run): ${problems.join(' | ')}`);
  const results = requirementProblems(graph, requirements, policy || { env }, { lastRunMs: lastRun?.ms || null });
  if (results.length) {
    const s = requirementSummary(results);
    lines.push(`Requirements: ${s.met} met · ${s.unmet} not met · ${s['needs-run']} need a run${s.unmet && env !== 'sandbox' ? ' — a structural miss refuses runs in this env' : ''}.`);
    for (const r of results) lines.push(`- [${r.status}] ${r.id} ${r.kind} "${r.text}" (${r.source}${r.storyId ? `, story ${r.storyId}` : ''}): ${r.detail}${r.blockIds?.length ? ` — blocks ${r.blockIds.join(', ')} (focus_canvas can show them)` : ''}`);
  } else {
    lines.push('Requirements: none declared.');
  }
  const est = estimateGraph(graph);
  lines.push(`Estimate: ~${Math.round(est.wallMs / 1000)}s longest path, ≤${est.modelCalls} model call${est.modelCalls === 1 ? '' : 's'}, ${est.humanWaits} human wait${est.humanWaits === 1 ? '' : 's'}.`);
  if (lastRun?.finishedAt) lines.push(`Last run: ${new Date(lastRun.finishedAt).toISOString()}${lastRun.ms != null ? `, ${Math.round(lastRun.ms / 1000)}s of compute` : ''}${lastRun.requirements ? `, requirements then ${lastRun.requirements.met}/${lastRun.requirements.total} met` : ''}.`);
  let out = lines.join('\n');
  if (maxChars > 0 && out.length > maxChars) {
    // Keep the head (shape + selected + verdicts) — drop connections first,
    // then block lines beyond the selected one.
    const short = [lines[0], lines[1], ...(selected && byId[selected] ? [`Selected: ${blockLine(byId[selected], { blockTypes, selected, verbose: true }).slice(2)}`] : []), ...lines.filter((l) => /^(agentOS|Requirements|- \[|Estimate|Last run)/.test(l))];
    out = short.join('\n');
    if (out.length > maxChars) out = `${out.slice(0, maxChars - 1)}…`;
  }
  return out;
}

// Blocks that are the "front" of a requirement row — what focus_canvas
// highlights when NoVA says "show me".
export function requirementBlocks(graph, requirements, policy, id) {
  const r = requirementProblems(graph, requirements, policy).find((x) => x.id === id);
  return r ? r.blockIds : [];
}

// describeSolution(solution, { apps, pipelines, verdicts, maxChars }) → text.
// `pipelines` carry { id, name, env, graph, requirements, workspaceId };
// `verdicts` is { [componentId]: { summary } } (optional — computed when
// policy is given instead); `rowResults` is [{ id, status, detail }] for the
// solution's own rows.
export function describeSolution(solution, opts = {}) {
  const { apps = [], pipelines = [], verdicts = {}, rowResults = [], maxChars = 0 } = opts;
  if (!solution) return 'No solution yet — "New solution" (or ask NoVA to engineer one) names the apps and workflows that make up the service.';
  const comps = solution.components || [];
  const appById = Object.fromEntries(apps.map((a) => [a.id, a]));
  const plById = Object.fromEntries(pipelines.map((p) => [p.id, p]));
  const nameOf = (c) => (c.kind === 'app' ? appById[c.refId]?.name : plById[c.refId]?.name) || `${c.kind} ${String(c.refId).slice(0, 6)}…`;
  const lines = [];
  lines.push(`Solution "${solution.name}" (${solution.status || 'draft'})${solution.goal ? ` — ${clip(solution.goal, 160)}` : ''}: ${comps.length} component${comps.length === 1 ? '' : 's'}, ${(solution.handoffs || []).length} handoff${(solution.handoffs || []).length === 1 ? '' : 's'}, ${(solution.requirements || []).length} requirement${(solution.requirements || []).length === 1 ? '' : 's'}.`);
  if (comps.length) {
    lines.push('Components:');
    for (const c of comps) {
      const v = verdicts[c.id]?.summary;
      const pl = c.kind === 'pipeline' ? plById[c.refId] : null;
      lines.push(`- ${c.id} · ${c.kind} "${nameOf(c)}"${c.role ? ` role ${c.role}` : ''}${pl ? ` (${(pl.graph?.blocks || []).length} blocks, ${pl.env || 'sandbox'}${v?.total ? `, requirements ${v.met}/${v.total} met${v.unmet ? `, ${v.unmet} not met` : ''}` : ', no requirements'}${(pl.graph?.blocks || []).some((b) => b.type === 'design.placeholder') ? ', has placeholders' : ''})` : ''}`);
    }
  } else lines.push('Components: none yet — add an app or a workflow, or "New service blueprint".');
  if ((solution.handoffs || []).length) {
    lines.push('Handoffs:');
    for (const h of solution.handoffs) {
      const f = comps.find((c) => c.id === h.from); const t = comps.find((c) => c.id === h.to);
      lines.push(`- ${h.id} · ${f ? nameOf(f) : h.from} → ${t ? nameOf(t) : h.to} via ${h.via}${h.note ? ` (${clip(h.note, 80)})` : ''}`);
    }
  }
  if ((solution.requirements || []).length) {
    lines.push('Solution requirements:');
    for (const r of solution.requirements) {
      const v = rowResults.find((x) => x.id === r.id);
      lines.push(`- [${v?.status || 'unknown'}] ${r.id} ${r.kind} "${r.text}" → ${(r.componentIds || []).length ? r.componentIds.map((id) => nameOf(comps.find((c) => c.id === id) || { kind: '?', refId: id })).join(', ') : 'all workflows'}${v?.detail ? `: ${clip(v.detail, 160)}` : ''}`);
    }
  } else lines.push('Solution requirements: none yet.');
  let out = lines.join('\n');
  if (maxChars > 0 && out.length > maxChars) out = `${out.slice(0, maxChars - 1)}…`;
  return out;
}
